import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronLeft, Download, Search } from 'lucide-react';
import type { UseMutationResult } from '@tanstack/react-query';
import type {
  PolicySettingsUpdate,
  PolicySettingsUpdateResult,
} from '@kirin/api-client/contracts/policies';
import { MAX_DISABLED_GITLEAKS_RULES } from '@kirin/api-client/contracts/policies';
import {
  GITLEAKS_RULES,
  GITLEAKS_RULE_GROUPS,
  type GitleaksRuleMeta,
} from '@kirin/api-client/data/gitleaks-rules';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Heading1 } from '@/components/shared/typography/Heading';
import { BodyMuted } from '@/components/shared/typography/BodyText';
import { ErrorText } from '@/components/shared/typography/ErrorText';
import { useToast } from '@/hooks/use-toast';
import { createMutationHandler } from '@/hooks/mutationHelpers';
import { triggerBlobDownload, toCsv } from '@/lib/download-utils';
import { cn } from '@/lib/utils';
import { STICKY_ABOVE_APP_FOOTER } from '@/components/footerLayout';
import { GITLEAKS_RULE_PARAM, withPolicyScope } from '@/lib/policyPaths';
import { policySettingsToFormData, type UsePolicySettingsHook } from '../types';

const SECRET_DETECTION_POLICY_KEY = 'secret-detection';
const DISABLED_RULES_SETTING_KEY = 'disabledGitleaksRules';

/** Sentinel for the "all categories" option — `Select` cannot hold an empty value. */
const ALL_GROUPS = '__all__';

/** The on/off segmented filter above the list. */
type StatusFilter = 'all' | 'enabled' | 'disabled';
const STATUS_FILTERS: readonly { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
];

interface GitleaksRulesPageProps {
  usePolicySettings: UsePolicySettingsHook;
  updateSettingsMutation: UseMutationResult<
    PolicySettingsUpdateResult,
    Error,
    PolicySettingsUpdate
  >;
  /** Whether the viewer may toggle rules. Downloads stay available either way. */
  canEdit: boolean;
  /** Path back to the Policies list. */
  policiesPath: string;
  /**
   * Who a save here applies to — "this organization", a security group's name,
   * or "your account". This route is mounted for all three and shows no scope
   * selector of its own, so the caller has to name the blast radius.
   */
  scopeLabel: string;
}

/** The columns the KIR-4396 spec names for the downloadable rule list. */
const CSV_COLUMNS = [
  ['rule_id', (rule: GitleaksRuleMeta) => rule.id],
  ['name', (rule: GitleaksRuleMeta) => rule.name],
  ['group', (rule: GitleaksRuleMeta) => rule.group],
  ['source', (rule: GitleaksRuleMeta) => rule.source],
  ['high_noise', (rule: GitleaksRuleMeta) => (rule.highNoise ? 'yes' : 'no')],
  ['description', (rule: GitleaksRuleMeta) => rule.description],
] as const;

/**
 * KIR-4590: the "Edit Secret Detection Rules" full page. Manages the built-in
 * ruleset ONLY — Kirin custom and secret-file rules keep their own editors.
 *
 * `disabledGitleaksRules` is a DENY list; the UI is an allow list of toggles.
 * That inversion happens once, at the load/save boundary.
 */
export function GitleaksRulesPage({
  usePolicySettings,
  updateSettingsMutation,
  canEdit,
  policiesPath,
  scopeLabel,
}: GitleaksRulesPageProps): ReactElement {
  const { toast } = useToast();
  const { data, isLoading, isError, error } = usePolicySettings(
    SECRET_DETECTION_POLICY_KEY
  );
  const [searchParams] = useSearchParams();
  const focusRuleId = searchParams.get(GITLEAKS_RULE_PARAM);

  const savedDisabled = useMemo<string[]>(() => {
    const setting = data?.settings.find(
      s => s.key === DISABLED_RULES_SETTING_KEY
    );
    // Mirrors the backend's `orgSettings[key] ?? defaultValue`. Treating "no
    // saved row" as "nothing disabled" would silently re-enable the rules
    // Kirin ships off, on the very first save.
    const value = setting?.value ?? setting?.defaultValue;
    return Array.isArray(value) ? (value as string[]) : [];
  }, [data]);

  const [disabled, setDisabled] = useState<ReadonlySet<string>>(
    () => new Set(savedDisabled)
  );

  useEffect(() => {
    setDisabled(new Set(savedDisabled));
  }, [savedDisabled]);

  const [search, setSearch] = useState(focusRuleId ?? '');
  const [group, setGroup] = useState<string>(ALL_GROUPS);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [highNoiseOnly, setHighNoiseOnly] = useState(false);
  // Collapsed, not expanded: every category starts open so the page still shows
  // the whole ruleset on arrival, and a deep-linked rule is never hidden.
  const [collapsedGroups, setCollapsedGroups] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  const focusRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // Not implemented everywhere (jsdom); the filter already isolated the rule.
    if (focusRuleId && typeof focusRef.current?.scrollIntoView === 'function') {
      focusRef.current.scrollIntoView({ block: 'center' });
    }
  }, [focusRuleId]);

  // Sets, not lengths: the org-supplied value may contain duplicates, which
  // would read as permanently dirty.
  const savedSet = useMemo(() => new Set(savedDisabled), [savedDisabled]);
  const isDirty =
    disabled.size !== savedSet.size ||
    [...savedSet].some(id => !disabled.has(id));

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return GITLEAKS_RULES.filter(rule => {
      if (group !== ALL_GROUPS && rule.group !== group) return false;
      if (highNoiseOnly && !rule.highNoise) return false;
      if (status !== 'all' && disabled.has(rule.id) !== (status === 'disabled'))
        return false;
      if (needle === '') return true;
      return (
        rule.id.toLowerCase().includes(needle) ||
        rule.name.toLowerCase().includes(needle) ||
        rule.description.toLowerCase().includes(needle)
      );
    });
  }, [search, group, highNoiseOnly, status, disabled]);

  const grouped = useMemo(
    () =>
      GITLEAKS_RULE_GROUPS.map(name => ({
        name,
        rules: filtered.filter(rule => rule.group === name),
      })).filter(bucket => bucket.rules.length > 0),
    [filtered]
  );

  const toggleRule = (ruleId: string, enabled: boolean): void => {
    setDisabled(previous => {
      const next = new Set(previous);
      if (enabled) {
        next.delete(ruleId);
      } else {
        next.add(ruleId);
      }
      return next;
    });
  };

  /**
   * Bulk on/off. Scoped to the rules the current filters leave visible, so
   * "Disable all" under a category or search never reaches past what the admin
   * can see.
   */
  const setAllVisible = (enabled: boolean): void => {
    setDisabled(previous => {
      const next = new Set(previous);
      for (const rule of filtered) {
        if (enabled) {
          next.delete(rule.id);
        } else {
          next.add(rule.id);
        }
      }
      return next;
    });
  };

  const setGroupCollapsed = (name: string, collapsed: boolean): void => {
    setCollapsedGroups(previous => {
      const next = new Set(previous);
      if (collapsed) {
        next.add(name);
      } else {
        next.delete(name);
      }
      return next;
    });
  };

  const overLimit = disabled.size > MAX_DISABLED_GITLEAKS_RULES;

  const handleSave = async (): Promise<void> => {
    if (!canEdit || !data || overLimit) return;
    const settings = policySettingsToFormData(data);
    // Sorted: `getUserPolicySettings` decides `isCustomized` by JSON.stringify
    // equality against the sorted default.
    settings[DISABLED_RULES_SETTING_KEY] = [...disabled].sort();

    const handler = createMutationHandler(updateSettingsMutation, toast, {
      successMessage: 'Secret detection rules saved',
      errorMessage: 'Failed to save secret detection rules',
    });
    await handler({
      policyKey: SECRET_DETECTION_POLICY_KEY,
      settings,
    } as PolicySettingsUpdate);
  };

  const handleDiscard = (): void => setDisabled(new Set(savedDisabled));

  const downloadCsv = (): void => {
    triggerBlobDownload(
      new Blob([toCsv(GITLEAKS_RULES, CSV_COLUMNS)], {
        type: 'text/csv;charset=utf-8',
      }),
      'kirin-secret-detection-rules.csv'
    );
  };

  const downloadJson = (): void => {
    triggerBlobDownload(
      new Blob([JSON.stringify(GITLEAKS_RULES, null, 2)], {
        type: 'application/json',
      }),
      'kirin-secret-detection-rules.json'
    );
  };

  /*
    Counted over the catalog, not `disabled.size`. The saved deny list is kept
    verbatim on save, so it can carry ids this catalog version has never heard
    of (an older/newer gitleaks, or a hand-edited value); subtracting its size
    would under-report how many rules are actually on.
  */
  const disabledInCatalog = useMemo(
    () => GITLEAKS_RULES.filter(rule => disabled.has(rule.id)).length,
    [disabled]
  );
  const enabledCount = GITLEAKS_RULES.length - disabledInCatalog;

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Link
          to={withPolicyScope(
            `${policiesPath}?policy=${SECRET_DETECTION_POLICY_KEY}`,
            searchParams
          )}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="size-4" />
          Policies
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Heading1>Edit Secret Detection Rules</Heading1>
          <Badge variant="outline" className="font-normal">
            Applies to {scopeLabel}
          </Badge>
        </div>
        <BodyMuted>
          These are the built-in secret detection rules that run on the device.
          Turn a rule on or off for {scopeLabel}. A disabled rule stops firing
          alerts on every IDE and coding agent it applies to. Your custom secret
          rules and secret-file rules are managed separately.
        </BodyMuted>
      </div>

      {isError && (
        <ErrorText as="p">
          {error instanceof Error ? error.message : 'Could not load settings.'}
        </ErrorText>
      )}

      {isLoading ? (
        <Card>
          <CardContent className="pt-6">
            <BodyMuted>Loading…</BodyMuted>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-56 flex-1">
                <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-8"
                  placeholder="Search by rule ID, name or description"
                  aria-label="Search secret detection rules"
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                />
              </div>
              <Select value={group} onValueChange={setGroup}>
                <SelectTrigger className="w-56" aria-label="Filter by category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_GROUPS}>All categories</SelectItem>
                  {GITLEAKS_RULE_GROUPS.map(name => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div
                role="group"
                aria-label="Filter by rule state"
                className="inline-flex items-center rounded-md border p-0.5"
              >
                {STATUS_FILTERS.map(option => (
                  <Button
                    key={option.value}
                    type="button"
                    size="sm"
                    variant={status === option.value ? 'secondary' : 'ghost'}
                    aria-pressed={status === option.value}
                    className="h-8 px-3"
                    onClick={() => setStatus(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={highNoiseOnly}
                  onCheckedChange={setHighNoiseOnly}
                  aria-label="Show only high-noise rules"
                />
                High noise only
              </label>
            </div>

            <div className="flex flex-wrap items-center gap-2 border-b pb-3">
              <BodyMuted>
                <span className="font-medium text-foreground">
                  {enabledCount}
                </span>{' '}
                of {GITLEAKS_RULES.length} rules enabled · {disabledInCatalog}{' '}
                disabled for {scopeLabel}
              </BodyMuted>
              <div className="ml-auto flex items-center gap-2">
                <Button type="button" variant="outline" onClick={downloadCsv}>
                  <Download className="mr-2 size-4" />
                  CSV
                </Button>
                <Button type="button" variant="outline" onClick={downloadJson}>
                  <Download className="mr-2 size-4" />
                  JSON
                </Button>
                {canEdit && (
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setAllVisible(true)}
                      disabled={filtered.length === 0}
                    >
                      Enable all
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setAllVisible(false)}
                      disabled={filtered.length === 0}
                    >
                      Disable all
                    </Button>
                  </>
                )}
              </div>
            </div>

            {overLimit && (
              <ErrorText as="p">
                At most {MAX_DISABLED_GITLEAKS_RULES} rules can be turned off.
                Turn some back on before saving.
              </ErrorText>
            )}

            {grouped.length === 0 ? (
              <BodyMuted>No rules match these filters.</BodyMuted>
            ) : (
              // The ruleset is 222 rows: scroll it inside the card so the
              // save/discard footer below never leaves the viewport.
              <div
                data-testid="gitleaks-rule-list"
                className="max-h-[60vh] space-y-3 overflow-y-auto pr-1"
              >
                {grouped.map(bucket => {
                  const isOpen = !collapsedGroups.has(bucket.name);
                  const onCount = bucket.rules.filter(
                    rule => !disabled.has(rule.id)
                  ).length;
                  return (
                    <Collapsible
                      key={bucket.name}
                      open={isOpen}
                      onOpenChange={open =>
                        setGroupCollapsed(bucket.name, !open)
                      }
                      className="space-y-2"
                    >
                      <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-md py-1 text-left hover:bg-muted/50">
                        <ChevronDown
                          className={
                            isOpen
                              ? 'size-4 text-muted-foreground'
                              : 'size-4 -rotate-90 text-muted-foreground'
                          }
                        />
                        <span className="text-sm font-semibold">
                          {bucket.name}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {onCount} of {bucket.rules.length} on
                        </span>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="divide-y rounded-md border">
                          {bucket.rules.map(rule => {
                            const isEnabled = !disabled.has(rule.id);
                            const isFocused = rule.id === focusRuleId;
                            return (
                              <div
                                key={rule.id}
                                ref={isFocused ? focusRef : undefined}
                                data-testid={`gitleaks-rule-${rule.id}`}
                                className={
                                  isFocused
                                    ? 'flex items-start gap-3 bg-muted/50 p-3'
                                    : 'flex items-start gap-3 p-3'
                                }
                              >
                                <Switch
                                  className="mt-1"
                                  checked={isEnabled}
                                  disabled={!canEdit}
                                  onCheckedChange={next =>
                                    toggleRule(rule.id, next)
                                  }
                                  aria-label={`${rule.name} (${rule.id})`}
                                />
                                <div className="min-w-0 space-y-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-sm font-medium">
                                      {rule.name}
                                    </span>
                                    <code className="rounded bg-muted px-1 py-0.5 text-xs text-muted-foreground">
                                      {rule.id}
                                    </code>
                                    {rule.highNoise && (
                                      <Badge variant="outline">
                                        High noise
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground">
                                    {rule.description}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  );
                })}
              </div>
            )}

            {canEdit && (
              <div
                // KIR-4902: clear the fixed app footer (z-30) instead of
                // sliding under it.
                className={cn(
                  STICKY_ABOVE_APP_FOOTER,
                  'z-10 -mx-4 -mb-4 flex justify-end gap-2 border-t bg-card px-4 py-4'
                )}
              >
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDiscard}
                  disabled={!isDirty || updateSettingsMutation.isPending}
                >
                  Discard changes
                </Button>
                <Button
                  type="button"
                  onClick={handleSave}
                  disabled={
                    !isDirty || overLimit || updateSettingsMutation.isPending
                  }
                >
                  {updateSettingsMutation.isPending
                    ? 'Saving…'
                    : 'Save changes'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
